/**
 * @file _funding-matcher.js
 * @description Matches user goal eligibility against funding tags in the page HTML.
 *
 * Reads data-goal-target attributes from the funding eligibility container and
 * cross-references them with the user's stored goal eligibility data.
 *
 * @requires window.QLD.eligibilityStorage - EligibilityStorage singleton (from _eligibility-storage.js)
 * @exposes window.QLD.fundingMatcher - FundingMatcher singleton
 *
 * @example
 * // Returns { status: 'has_matches', eligible: [{ target, label }], ineligible: [{ target, label }] }
 * window.QLD.fundingMatcher.findPersonalFunding([
 *     { target: 'career-start', label: 'Career Start' },
 *     { target: 'fee-free-tafe', label: 'Fee-Free TAFE' }
 * ]);
 */

(function () {
    'use strict';
    class FundingMatcher {
        get storage() {
            return window.QLD && window.QLD.eligibilityStorage;
        }

        /**
         * Find personal funding matches from a list of HTML funding targets.
         *
         * @param {Array<{target: string, label: string}>} targets - Targets from data-goal-target attributes
         * @returns {Object} result
         *   - status:  'no_data' | 'no_funding' | 'has_matches' | 'no_matches'
         *   - eligible: Array<{ target, label }>
         *   - ineligible: Array<{ target, label }>
         */
        findPersonalFunding(targets) {
            if (!this.storage || !this.storage.hasData()) {
                return { status: 'no_data', eligible: [], ineligible: [] };
            }

            if (!targets || targets.length === 0) {
                return { status: 'no_funding', eligible: [], ineligible: [] };
            }

            const allGoals = this.storage.getGoals();
            const eligible = [];
            const ineligible = [];

            for (const { target, label } of targets) {
                const matchingGoals = allGoals.filter(g => g.target === target);
                if (matchingGoals.length === 0) continue;

                const hasAllowed = matchingGoals.some(g => g.status === 'allowed');
                if (hasAllowed) {
                    eligible.push({ target, label });
                } else {
                    ineligible.push({ target, label });
                }
            }

            return {
                status: eligible.length > 0 ? 'has_matches' : 'no_matches',
                eligible,
                ineligible
            };
        }
    }

    // Create singleton
    window.QLD = window.QLD || {};
    window.QLD.fundingMatcher = new FundingMatcher();

})();